import * as React from 'react';
import { useUserCanPerformOrgAction } from '@/utils/hooks/useUserCanPerformAction';
import { useDispatch, useSelector } from 'react-redux';
import Checkbox from 'antd/lib/checkbox';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { LoadingOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { RootState } from '@/reducers/reducers';
import { selectOrg } from '@/selectors/selectors';
import { OrgComponentProps } from '@/typescriptHelpers';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { SetDisableDirectorySyncGroupMappingRequest, tryUpdateOrgSettings } from '@/actions/orgSettings';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

const ENABLE_CONFIRM_CONTENT =
    'Runway user groups will immediately be reassigned from your directory groups. Any user group assigned manually in Runway that is not part of a directory group mapping will be removed.';

const DISABLE_CONFIRM_CONTENT =
    'Directory Sync will stop assigning Runway user groups. Group assignments users already have are kept as-is, and from then on user groups are managed manually in Runway.';

export const DirectorySyncGroupMappingSetting: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const dispatch = useDispatch();
    // Static Modal.confirm renders outside the React tree and misses our ConfigProvider theme
    const [modal, modalContextHolder] = Modal.useModal();
    const mappingEnabled = useSelector(
        (state: RootState) => !selectOrg(state, props.orgId)?.ssoSamlConfig?.disableDirectorySyncGroupMapping,
    );
    const canUpdate = useUserCanPerformOrgAction(RunwayUserAction.UAUpdateDirectorySyncUserGroupsForGroups);

    const isLoading = useIsLoading(OrgSettingsUpdatable.SetDisableDirectorySyncGroupMapping);
    const error = useError(OrgSettingsUpdatable.SetDisableDirectorySyncGroupMapping);

    const [isFieldChecked, setIsFieldChecked] = React.useState(mappingEnabled);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
            setIsFieldChecked(mappingEnabled);
        }
    }, [error]);

    React.useEffect(() => {
        setIsFieldChecked(mappingEnabled);
    }, [mappingEnabled]);

    const updateSetting = (enabled: boolean) => {
        setIsFieldChecked(enabled);
        const request: SetDisableDirectorySyncGroupMappingRequest = { orgId: props.orgId, disabled: !enabled };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.SetDisableDirectorySyncGroupMapping, request));
    };

    const onCheckboxChanged = (e: CheckboxChangeEvent) => {
        const enabled = e.target.checked;
        modal.confirm({
            title:
                enabled ?
                    'Start mapping directory groups to Runway user groups?'
                :   'Stop mapping directory groups to Runway user groups?',
            icon: null,
            content: enabled ? ENABLE_CONFIRM_CONTENT : DISABLE_CONFIRM_CONTENT,
            okText: enabled ? 'Start mapping' : 'Stop mapping',
            okButtonProps: { type: 'primary' },
            cancelText: 'Cancel',
            styles: { content: { padding: 24 } },
            onOk: () => updateSetting(enabled),
        });
    };

    return (
        <Space direction="vertical" size={4}>
            {modalContextHolder}
            <Space size={8}>
                <Tooltip
                    title={canUpdate ? null : 'You need the IT Admin permission in Runway to change this setting.'}
                >
                    <Checkbox checked={isFieldChecked} disabled={isLoading || !canUpdate} onChange={onCheckboxChanged}>
                        Enable directory groups to Runway groups mapping
                    </Checkbox>
                </Tooltip>
                {isLoading && (
                    <Text type="secondary">
                        <LoadingOutlined spin style={{ color: antGrey.grey6, marginRight: 6 }} />
                        {isFieldChecked ? 'Reapplying group mapping…' : 'Turning off group mapping…'}
                    </Text>
                )}
            </Space>
            <Text type="secondary">
                {isFieldChecked ?
                    'User groups are being managed by your directory provider.'
                :   'Directory groups are ignored, and user groups are managed directly in Runway from Org settings > Team.'
                }
            </Text>
        </Space>
    );
};
